/**
 * Course Domain Model
 * Represents a complete course with sections and questions
 */

export class Course {
  constructor(data) {
    this.id = data.id;
    this.title = data.title;
    this.description = data.description;
    this.version = data.version;
    this.metadata = data.metadata || {};
    if (!this.metadata.questionPoolScope) {
      this.metadata.questionPoolScope = 'unlocked';
    }
    this.createdAt = data.createdAt || new Date().toISOString();
    this.updatedAt = data.updatedAt || new Date().toISOString();
    
    // Process sections and enrich questions with sectionId
    // CRITICAL: Ensure each section has a valid index
    this.sections = (data.sections || []).map((section, arrayIndex) => {
      // Enrich questions with sectionId
      if (section.questions && Array.isArray(section.questions)) {
        section.questions = section.questions.map(question => ({
          ...question,
          sectionId: section.id,
        }));
      }
      
      // CRITICAL: Ensure index is set, use array position if not present
      if (typeof section.index !== 'number') {
        console.warn(`[Course] Section ${section.id} missing index property, using array position ${arrayIndex}`, {
          sectionId: section.id,
          arrayIndex,
        });
        section.index = arrayIndex;
      }
      
      return section;
    });
  }

  /**
   * Get section by ID
   */
  getSectionById(sectionId) {
    return this.sections.find(s => s.id === sectionId);
  }

  /**
   * Get section by index
   */
  getSectionByIndex(index) {
    return this.sections.find(s => s.index === index);
  }

  /**
   * Get all questions for a section
   */
  getQuestionsForSection(sectionId) {
    const section = this.getSectionById(sectionId);
    return section ? section.questions || [] : [];
  }

  /**
   * Get question by ID
   */
  getQuestionById(questionId) {
    for (const section of this.sections) {
      const question = section.questions?.find(q => q.id === questionId);
      if (question) return question;
    }
    return null;
  }

  /**
   * Convert to JSON
   */
  toJSON() {
    return {
      id: this.id,
      title: this.title,
      description: this.description,
      version: this.version,
      sections: this.sections,
      metadata: this.metadata,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };
  }

  /**
   * Create from JSON
   */
  static fromJSON(json) {
    return new Course(json);
  }
}
